import type { CSSProperties, ReactNode } from 'react';
import { CRAPS_BETS, type CrapsBetId, type CrapsRules } from '@tv-casino/shared';
import type { CrapsSpotMark, CrapsStack } from '@tv-casino/viewmodels/components/crapsFelt.js';
import {
  crapsBoxSpots,
  crapsDontLineSpots,
  crapsFieldOdds,
  crapsHardSpots,
  crapsPassLineSpots,
  crapsPropSpots,
  crapsSpotParked,
} from '@tv-casino/viewmodels/components/crapsFelt.js';
import { chipStyle, useSeatHue } from '../lib/playerColors.js';
import styles from './CrapsTable.module.css';

export type { CrapsSpotMark, CrapsStack };

export interface CrapsTableProps {
  rules: CrapsRules;
  /** The point, or null on the come-out: the puck rides on the box it names. */
  point: number | null;
  /** Every chip on the felt, spot by spot. */
  stacks: Partial<Record<CrapsBetId, CrapsStack[]>>;
  /** Spots the last roll settled, for the win/loss flash. */
  marks?: Partial<Record<CrapsBetId, CrapsSpotMark>>;
  /** The spot the next chip would land on. */
  armed?: CrapsBetId | null;
  /** Phone only: is this spot taking chips right now? */
  canDrop?: (id: CrapsBetId) => boolean;
  onDrop?: (id: CrapsBetId) => void;
  /** Phone only: take this seat's chips back off the spot. */
  onPull?: (id: CrapsBetId) => void;
  canPull?: (id: CrapsBetId) => boolean;
  /** The TV renders the same felt twice the size. */
  size?: 'phone' | 'tv';
  /** The dice, the puck's OFF state, the stickman's call — whatever sits above. */
  head?: ReactNode;
}

const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(' ');

/**
 * A craps layout, drawn the way it is printed on a real table: box numbers
 * along the top, the proposition box in the middle, the field under it and the
 * two lines running along the bottom rail. It is half a table — the end one
 * player stands at — so the spots are big enough to read and to hit.
 *
 * Every chip on the felt is drawn where it was bet, whoever bet it. The phone
 * passes handlers and gets a betting board; the TV passes none and gets the
 * same picture, read-only.
 */
export function CrapsTable({
  rules,
  point,
  stacks,
  marks,
  armed = null,
  canDrop,
  onDrop,
  onPull,
  canPull,
  size = 'phone',
  head,
}: CrapsTableProps) {
  // A chip on the felt wears its player's colour, never its denomination —
  // the same hue the seat's plate carries. The phone and the TV are looking
  // at one table, so a stack has to read as the same chip on both.
  const hue = useSeatHue();
  const playerStyle = (seatIndex: number): CSSProperties => chipStyle(hue(seatIndex));
  const live = Boolean(onDrop);

  const spot = (id: CrapsBetId, label: string, extra?: string, key?: string) => {
    const here = stacks[id] ?? [];
    const mine = here.find((s) => s.mine) ?? null;
    const others = here.filter((s) => !s.mine);
    const open = live && (canDrop?.(id) ?? true);
    const mark = marks?.[id];
    // Anyone's chips, not just this phone's: the TV holds no seat and has to
    // show a sleeping bet as plainly as the phone that owns it.
    const parked = crapsSpotParked(id, point, here);
    const body = (
      <>
        <span className={styles.label}>{label}</span>
        {extra && <span className={styles.sub}>{extra}</span>}
        {id === `place${point}` && (
          <span className={styles.puck} aria-label={`The puck is ON the ${point}`}>
            ON
          </span>
        )}
        {parked && (
          <span className={styles.offTag} aria-label="Off for the come-out">
            OFF
          </span>
        )}
        <span className={styles.chips}>
          {others.slice(0, 3).map((s) => (
            <span
              key={s.seatIndex}
              className={cx(styles.chip, styles.chipPlayer)}
              style={playerStyle(s.seatIndex)}
              title={`${s.name ?? `Seat ${s.seatIndex + 1}`} · ${s.amount}`}
            >
              {s.amount}
            </span>
          ))}
          {others.length > 3 && <span className={styles.more}>+{others.length - 3}</span>}
        </span>
      </>
    );

    const cell = cx(
      styles.spot,
      armed === id && styles.spotArmed,
      mine && styles.spotMine,
      parked && styles.spotParked,
      mark === 'win' && styles.spotWon,
      mark === 'lose' && styles.spotLost,
      live && !open && styles.spotShut,
    );

    return (
      <div className={cell} data-craps-spot={id} key={key ?? id}>
        {live ? (
          <button
            type="button"
            className={styles.drop}
            disabled={!open}
            onClick={() => onDrop?.(id)}
            aria-label={`${CRAPS_BETS[id].label} — ${CRAPS_BETS[id].odds}`}
          >
            {body}
          </button>
        ) : (
          <div className={styles.drop}>{body}</div>
        )}
        {mine &&
          (onPull && (canPull?.(id) ?? true) ? (
            <button
              type="button"
              className={cx(styles.myChip, styles.chipPlayer)}
              style={playerStyle(mine.seatIndex)}
              onClick={() => onPull(id)}
              aria-label={`Take back ${mine.amount} on ${CRAPS_BETS[id].label}`}
            >
              {mine.amount}
            </button>
          ) : (
            <span
              className={cx(styles.myChip, styles.myChipLocked, styles.chipPlayer)}
              style={playerStyle(mine.seatIndex)}
              aria-label={`${mine.amount} on ${CRAPS_BETS[id].label}`}
            >
              {mine.amount}
            </span>
          ))}
      </div>
    );
  };

  return (
    <div className={cx(styles.felt, size === 'tv' && styles.feltTv)}>
      {head && <div className={styles.head}>{head}</div>}

      {rules.placeEnabled && (
        <div className={styles.boxRow} role="group" aria-label="Box numbers">
          {crapsBoxSpots().map((cell) => spot(cell.id, cell.label, cell.odds))}
        </div>
      )}

      {(rules.hardwaysEnabled || rules.propsEnabled) && (
        <div className={styles.center}>
          {rules.hardwaysEnabled && (
            <div className={styles.hardBox} role="group" aria-label="Hardways">
              <span className={styles.boxTitle}>Hardways</span>
              <div className={styles.hardGrid}>
                {crapsHardSpots().map((cell) => spot(cell.id, cell.label, cell.odds))}
              </div>
            </div>
          )}
          {rules.propsEnabled && (
            <div className={styles.propBox} role="group" aria-label="One-roll props">
              <span className={styles.boxTitle}>One roll</span>
              <div className={styles.propGrid}>
                {crapsPropSpots().map((cell) => spot(cell.id, cell.label, cell.odds))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className={styles.fieldRow}>{spot('field', 'FIELD', crapsFieldOdds(rules))}</div>

      <div className={styles.lineRow}>
        {crapsDontLineSpots().map((cell) => spot(cell.id, cell.label, cell.odds))}
      </div>

      <div className={styles.lineRow}>
        {crapsPassLineSpots().map((cell) => spot(cell.id, cell.label, cell.odds))}
      </div>
    </div>
  );
}
